/**
 * Mission Create Slice (GW-8603, reworked GW-8633)
 *
 * Owns ONLY the launch-lifecycle UI state for MissionCreatePage's
 * handleLaunch flow — launching/error/launched. The mission briefing draft
 * itself stays in missionBriefingSlice; this slice is deliberately narrower
 * and ephemeral (not persisted via store.ts partialize), so a page refresh
 * mid-launch does not resurrect a stale launching/error/launched state.
 *
 * GW-8633: mission creation and run creation are now a single logical
 * transaction (PRP-GW8581-02 FR-11) — launchSucceeded() only fires once
 * BOTH calls have succeeded, immediately before navigating to the run-detail
 * route. A mission-succeeds/run-fails outcome calls launchFailed() instead
 * (never launchSucceeded()), so the user stays on this page with a visible
 * error and the briefing intact for a retry — there is no longer an
 * intermediate "mission created but run pending" state this slice tracks.
 *
 * Action semantics map directly onto MissionCreatePage's handleLaunch:
 * - startLaunch(): called at the top of handleLaunch — sets launching true
 *   and clears any previous error.
 * - launchSucceeded(): called only after BOTH mission creation and run
 *   creation have succeeded, right before navigating to the run-detail
 *   route — sets launched true and launching false. `launched` (rather than
 *   relying on the navigate call alone) is what lets a re-render during the
 *   in-flight navigation avoid redirecting away (see MissionCreatePage's
 *   briefingMatchesUrl comment) before React Router finishes swapping
 *   routes.
 * - launchFailed(error): called from either failure branch — mission
 *   creation failing outright, or mission creation succeeding but run
 *   creation failing — records the error message for display and clears
 *   launching. Never followed by launchSucceeded() or navigation for the
 *   same launch attempt.
 * - resetLaunch(): returns all three fields to their initial values.
 */

import type { StateCreator } from 'zustand';

export interface MissionCreateSlice {
  launching: boolean;
  error: string | null;
  launched: boolean;
  startLaunch: () => void;
  launchSucceeded: () => void;
  launchFailed: (error: string) => void;
  resetLaunch: () => void;
}

const initial: Pick<MissionCreateSlice, 'launching' | 'error' | 'launched'> = {
  launching: false,
  error: null,
  launched: false,
};

export const createMissionCreateSlice: StateCreator<
  MissionCreateSlice,
  [],
  [],
  MissionCreateSlice
> = (set) => ({
  ...initial,
  startLaunch: () => set({ launching: true, error: null }),
  launchSucceeded: () => set({ launched: true, launching: false }),
  launchFailed: (error) => set({ error, launching: false }),
  resetLaunch: () => set({ ...initial }),
});
